import * as React from "react";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useColorScheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import Popover from "@mui/material/Popover";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import LanguageIcon from "@mui/icons-material/Language";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardIcon from "@mui/icons-material/Dashboard";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import * as Muicon from "@mui/icons-material";
import Badge from '@mui/material/Badge';
import AppContext from "../utils/context";
import { useTheme } from '@mui/material/styles';

export default function MainMenu() {
  const appContext = React.useContext(AppContext);
  const { t, i18n } = useTranslation();
  const { events } = appContext;

  const [workspaces, setWorkspaces] = React.useState([]);
  const [workspacesOpen, setWorkspacesOpen] = React.useState([]);
  const [selectedProject, setSelectedProject] = React.useState(null);
  
  const [anchorElWorkspaceAction, setAnchorElWorkspaceAction] = React.useState(null);
  const [workspaceMenu, setWorkspaceMenu] = React.useState(null);
  const [anchorElWorkspacePopover, setAnchorElWorkspacePopover] = React.useState(null);
  const [anchorElLang, setAnchorElLang] = React.useState(null);

  const { mode, setMode } = useColorScheme();  
  const theme = useTheme();
  const resolvedTheme = theme.palette.mode;

  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();

  const [pages, setPages] = React.useState([]);
  const [collapsed, setCollapsed] = React.useState(false);
  const isSmall = useMediaQuery("(max-width:900px)");

  const availableLangs = React.useMemo(
    () => Object.keys(i18n.options?.resources ?? {}),
    [i18n]
  );

  React.useEffect(() => setCollapsed(isSmall), [isSmall]);

  React.useEffect(() => {
    if (appContext?.user) {
      setPages(appContext.user.menu);
      setWorkspaces(appContext.user.workspaces);
    }
  }, [appContext?.user]);

  /* Synchronisation URL → menu */
  React.useEffect(() => {
    if (projectId && workspaces.length) {
      const ws = workspaces.find(w =>
        w.projects.some(p => String(p.id) === String(projectId))
      );
      if (ws) {
        setWorkspacesOpen([ws.id]);
        setSelectedProject(projectId);
      }
    } else {
      setSelectedProject(null);
    }
  }, [location.pathname, workspaces]);

  const toggleCollapse = () => setCollapsed(!collapsed);
  const toggleColorMode = () => setMode(mode === "dark" ? "light" : "dark");

  const openProject = (project) => {
    navigate(`/project/${project.id}`);
    setSelectedProject(project.id);
  };

  const search = () => {
    events.dispatch("search", {
      callbacks: {
        workspace: (workspace) => setWorkspacesOpen([workspace.workspace_id]),
        project: (project) => {
          navigate(`/project/${project.project_id}`);
          setSelectedProject(project.project_id);
        },
        instance: (instance) => navigate(`/project/${instance.project_id}?instance_id=${instance.instance_id}`),
      },
    });
  };

  const handleOpenWorkspaceAction = (event, workspace) => {
    setAnchorElWorkspaceAction(event.currentTarget);
    setWorkspaceMenu(workspace);
  };
  const handleCloseWorkspaceAction = () => {
    setAnchorElWorkspaceAction(null);
    setWorkspaceMenu(null);
  };

  const toggleWorkspace = (workspace) => {
    if (workspacesOpen.includes(workspace.id)) {
      setWorkspacesOpen(workspacesOpen.filter((i) => i !== workspace.id));
    } else {
      setWorkspacesOpen([workspace.id]);
    }
  };


  /* Wrapper pour item afin de gérer l'affichage icône/texte + tooltip en mode mini */
  const Item = ({ label, icon, onClick, tooltip }) => {
    const content = (
      <ListItemButton onClick={onClick} sx={{ justifyContent: collapsed ? "center" : "flex-start" }}>
        <ListItemIcon sx={{ minWidth: 0, justifyContent: "center" }}>{icon}</ListItemIcon>
        {!collapsed && <ListItemText sx={{ ml: 2 }} primary={label} />}
      </ListItemButton>
    );
    return collapsed ? (
      <Tooltip title={tooltip || label} placement="right"><ListItem disablePadding>{content}</ListItem></Tooltip>
    ) : (
      <ListItem disablePadding>{content}</ListItem>
    );
  };

  return (
    <Box
      sx={{
        width: collapsed ? 72 : 300,
        flexBasis: collapsed ? 72 : 300,
        flexShrink: 0,
        padding: "20px 0 20px 20px",
        transition: "width .2s, flex-basis .2s",
      }}
    >
      <Paper sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header : Logo uniquement */}
        <List sx={{ p: 0 }}>
          <ListItem disablePadding>
            <ListItemButton onClick={() => navigate(`/`)} sx={{ justifyContent: collapsed ? "center" : "flex-start" }}>
              {window?.api?.app_logo && (() => {
                const logo = resolvedTheme === 'dark'                 
                    ? (collapsed ? window.api.app_logo_small_dark : window.api.app_logo_dark)
                    : (collapsed ? window.api.app_logo_small : window.api.app_logo);
                return (
                  <img
                    src={logo}
                    alt="Logo"
                    style={{
                      height: collapsed ? 32 : 48,
                      transition: "height .2s",
                      marginRight: collapsed ? 0 : 10,
                    }}
                  />
                );
              })()}
              {!collapsed && <Box sx={{ fontWeight: 800 }}>{window?.api?.app_name ?? ""}</Box>}
            </ListItemButton>
          </ListItem>
        </List>

        <Divider />

        {/* Navigation principale */}
        <List sx={{ p: 0, flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <ListItem disablePadding secondaryAction={!collapsed && (
            <IconButton edge="end" size="small" onClick={search}><SearchIcon /></IconButton>
          )}>
            <ListItemButton onClick={() => navigate(`/`)} sx={{ justifyContent: collapsed ? "center" : "flex-start" }}>
              <ListItemIcon sx={{ minWidth: 0, justifyContent: "center" }}><DashboardIcon /></ListItemIcon>
              {!collapsed && <ListItemText sx={{ ml: 2 }} primary={t("menu.dashboard")} />}
            </ListItemButton>
          </ListItem>
          {collapsed && <Item icon={<SearchIcon />} onClick={search} tooltip={t("menu.search")} />}

          {/* Workspaces */}
          {collapsed ? (
            <Item icon={<FormatListBulletedIcon />} onClick={(e) => setAnchorElWorkspacePopover(e.currentTarget)} tooltip={t("menu.workspaces")} />
          ) : (
            <div style={{ overflow: "auto", flexGrow: 1, height: 0 }}>
              {workspaces.map((workspace) => (
                <React.Fragment key={workspace.id}>
                  <ListItem disablePadding secondaryAction={<IconButton edge="end" size="small" onClick={(e) => handleOpenWorkspaceAction(e, workspace)}><MoreVertIcon /></IconButton>}>
                    <ListItemButton onClick={() => toggleWorkspace(workspace)}>
                      <ListItemText primary={workspace.name} />
                      {workspacesOpen.includes(workspace.id) ? <Muicon.ExpandLess /> : <Muicon.ExpandMore />}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={workspacesOpen.includes(workspace.id)} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {workspace.projects.map((project) => (
                        <ListItemButton key={project.id}
                          selected={String(project.id) === String(selectedProject)}
                          sx={{
                            pl: 4,
                            '&.Mui-selected, &.Mui-selected:hover': {
                              bgcolor: 'primary.100',          // ou une couleur hexadécimale
                            }
                          }} onClick={() => openProject(project)}>
                          <ListItemText primary={project.name} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </React.Fragment>
              ))}</div>
          )}

          <Menu anchorEl={anchorElWorkspaceAction} open={Boolean(anchorElWorkspaceAction)} onClose={handleCloseWorkspaceAction} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
            {workspaceMenu && workspaceMenu.is_manager && (
              <MenuItem onClick={() => {
                events.dispatch("create-project", {
                  data: workspaceMenu,
                  callback: (result) => {
                    appContext.events.dispatch("refresh-user");
                    navigate(`/project/${result.id}`);
                  },
                });
                handleCloseWorkspaceAction();
              }}>{t("menu.project.new")}</MenuItem>
            )}
            <MenuItem onClick={() => {
              navigate(`/logs?workspace_id=${workspaceMenu.id}`);
              handleCloseWorkspaceAction();
            }}>{t("commons.logs")}</MenuItem>
          </Menu>
        </List>

        {/* Navigation secondaire */}
        <Divider />
        <List sx={{ p: 0 }}>
          {pages.map((page) => {
            const Icon = Muicon[page.icon] || Muicon.Notifications;
            return (
              <Item
              key={page.url}
              icon={<Icon />}
              label={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{flexGrow:1}}>{t(`menu.${page.label}`)}</Box>
                {page.badge && (
                  <Badge
                  badgeContent={page.badge}
                  color="primary"
                  sx={{ mr: 1 }}
                  />
                )}
                </Box>
              }
              tooltip={t(`menu.${page.label}`)}
              onClick={() => {
                if (page.target === "_blank") {
                window.open(page.url, "_blank");
                } else {
                navigate(page.url);
                }
              }}
              />
            );
          })}

          <ListItem disablePadding secondaryAction={!collapsed && (
            <IconButton edge="end" size="small" onClick={() => appContext.database.auth.signOut()}><ExitToAppIcon /></IconButton>
          )}>
            <ListItemButton onClick={() => window?.api?.account_url && window.open(window.api.account_url, "_blank")} sx={{ justifyContent: collapsed ? "center" : "flex-start" }}>
              <ListItemIcon sx={{ minWidth: 0, justifyContent: "center" }}><PersonIcon /></ListItemIcon>
              {!collapsed && <ListItemText sx={{ ml: 2 }} primary={t("menu.account")} />}
            </ListItemButton>
          </ListItem>
          {collapsed && <Item icon={<ExitToAppIcon />} onClick={() => appContext.database.auth.signOut()} tooltip={t("menu.logout")} />}
        </List>

        <Divider />

        {/* Footer : dark/light, langues, collapse */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-end", p: 1 }}>
          {!collapsed && (
            <>
              <div style={{ flexGrow: 1 }}><small>{window?.api?.app_version && window.api.app_version}</small></div>
              <IconButton size="small" onClick={toggleColorMode}>
                 {resolvedTheme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
              <IconButton size="small" onClick={(e) => setAnchorElLang(e.currentTarget)}>
                 <Typography variant="button">
                {i18n.language.toUpperCase()}
                 </Typography>
              </IconButton>
            </>
          )}
          <IconButton size="small" onClick={toggleCollapse}>{collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}</IconButton>
        </Box>

        <Menu anchorEl={anchorElLang}
          open={Boolean(anchorElLang)}
          onClose={() => setAnchorElLang(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          {availableLangs.map((lng) => (
            <MenuItem key={lng}
              selected={i18n.language === lng}
              onClick={() => { i18n.changeLanguage(lng); setAnchorElLang(null); }}>
              {lng.toUpperCase()}
            </MenuItem>
          ))}
        </Menu>

        <Popover 
          open={Boolean(anchorElWorkspacePopover)} 
          anchorEl={anchorElWorkspacePopover} 
          onClose={() => setAnchorElWorkspacePopover(null)} 
          anchorOrigin={{ vertical: "top", horizontal: "right" }} 
          transformOrigin={{ vertical: "top", horizontal: "right" }}>
          <Box sx={{minWidth: 250, overflow: "auto" }}>
            {workspaces.map((workspace) => (
              <Box key={workspace.id}>
                <ListItemButton onClick={() => toggleWorkspace(workspace)}>
                  <ListItemText primary={workspace.name} />
                  {workspacesOpen.includes(workspace.id) ? <Muicon.ExpandLess /> : <Muicon.ExpandMore />}
                </ListItemButton>
                <Collapse in={workspacesOpen.includes(workspace.id)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {workspace.projects.map((project) => (
                      <ListItemButton key={project.id}
                        selected={String(project.id) === String(selectedProject)}
                        sx={{
                          pl: 4,
                          '&.Mui-selected, &.Mui-selected:hover': {
                            bgcolor: 'primary.100',          // ou une couleur hexadécimale
                          }
                        }}
                        onClick={() => { openProject(project); setAnchorElWorkspacePopover(null); }}>
                        <ListItemText primary={project.name} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </Box>
            ))}
          </Box>
        </Popover>
      </Paper>
    </Box>
  );
}
